import { Dialog, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ExchangeIcon, GoogleIcon } from 'components/icons';
import { GoogleCustomerIdSelector } from 'components/integrations/google';
import { ButtonCTASmall, LoadingSpinner, SpinnerSmall } from 'components/ui';
import { forEach } from 'lodash';
import querystring from 'querystring';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAddGoogleOAuthIntegrationMutation,
  useGetStoreInfoQuery,
  useUpdateIntegrationMutation,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { LocationState } from '../types';
import { GoogleCustomerIdsSelection } from './types';

export const GoogleConnect = () => {
  const { query } = useParseURLQueryParams();
  const history = useHistory();

  const segmentsClientID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const redirectURI = process.env.REACT_APP_GOOGLE_REDIRECT_URI;
  const oauthScope = process.env.REACT_APP_GOOGLE_SCOPE;
  const buttonRef = useRef(null);
  const [googleLoggedIn, setGoogleLoggedIn] = useState(false);
  const [googleCustomerIds, setGoogleCustomerIds] =
    useState<GoogleCustomerIdsSelection>({ selection: [] });

  const [addOAuthIntegration, { isLoading: isOAuthConfirmationLoading }] =
    useAddGoogleOAuthIntegrationMutation();

  const [updateIntegration, { isLoading: isUpdateLoading }] =
    useUpdateIntegrationMutation();

  const inputRef = useRef(null);

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const location = useLocation<LocationState>();

  const handleCloseModal = () => {
    if (location?.state?.background) {
      history.push(location.state.background);
    } else {
      history.replace({
        ...history.location,
        pathname: '/integrations/connect',
      });
    }
  };

  useEffect(() => {
    if (!!!query.code) return;

    let storeId = localStorage.getItem('google-integration');

    if (!!!storeId) return;

    addOAuthIntegration({
      provider: 'google',
      rawParams: {
        store_id: storeId,
        code: String(query.code),
      },
    })
      .unwrap()
      .then((response) => {
        setGoogleCustomerIds({
          selection: response.customer_ids.map((item) => ({
            id: item.id,
            selected: false,
            sync_status: item.sync_status,
          })),
        });
        setGoogleLoggedIn(true);
        toast.success('Google connected successfuly');
        localStorage.removeItem('google-integration');
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.code]);

  // StoreId saved in the Local storage, once there is no way to identify which store the user authorized just from the Mailchimp OAuth return url
  const handleGoogleLocalStorageSave = () => {
    localStorage.setItem('google-integration', String(storeInfo?.id));
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?scope=${oauthScope}&access_type=offline&include_granted_scopes=true&response_type=code&state=state_parameter_passthrough_value&redirect_uri=${redirectURI}&client_id=${segmentsClientID}&prompt=consent`;
  };

  const handleSubmit = () => {
    const customerIds: string[] = [];

    forEach(googleCustomerIds.selection, (item) => {
      if (item.selected) {
        customerIds.push(item.id);
      }
    });
    const payload = {
      customer_ids: customerIds.join(','),
    };
    updateIntegration({
      storeId: Number(storeInfo?.id),
      provider: 'google',
      body: payload,
    })
      .unwrap()
      .then(handleCloseModal)
      .catch(() =>
        toast.error(
          'Your google account is not authorized to create customer list',
          { toastId: 'api-error' }
        )
      );
  };

  const hasSelectedCustomerIds = googleCustomerIds.selection.some(
    (item) => item.selected
  );

  return (
    <Transition show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open
        initialFocus={inputRef}
        onClose={handleCloseModal}
      >
        <div className="min-h-screen text-center md:px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full my-8 overflow-auto text-left align-middle transition-all transform bg-white shadow-xl lg:overflow-hidden rounded-2xl md:h-segments-516px md:w-segments-648px">
              <div className="w-full h-48 px-5 pt-9 lg:px-12 bg-segments-accent-color-1 bg-opacity-5">
                <p className="text-black text-opacity-50 text-segments-13px">
                  Integrations
                </p>
                <div
                  className="flex items-center mt-8 space-x-2 focus:outline-none"
                  ref={buttonRef}
                >
                  <GoogleIcon />
                  <ExchangeIcon />
                  <p>{storeInfo?.name}</p>
                </div>
                <p className="mt-4 text-2xl">Connect to Google</p>
              </div>
              <div className="flex flex-col justify-between px-5 pt-8 text-gray-700 lg:px-12 h-80">
                {isOAuthConfirmationLoading ? (
                  <div
                    tabIndex={0}
                    className="flex justify-center mt-10 focus:outline-none"
                  >
                    <LoadingSpinner className="w-20 h-20 text-segments-accent-color-orange" />
                  </div>
                ) : (
                  <>
                    {!!!googleLoggedIn ? (
                      <div className="mx-auto text-center">
                        <button
                          className="mt-8"
                          ref={buttonRef}
                          onClick={handleGoogleLocalStorageSave}
                        >
                          <img
                            src="/assets/btn_google_signin_light_normal_web@2x.png"
                            alt="google button"
                            width="250"
                          />
                        </button>
                        <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl w-full">
                          <div className="space-y-3 ">
                            <div className="flex space-x-3">
                              <ExclamationCircleIcon className="w-5 h-5" />

                              <p className="text-black text-opacity-60">
                                Please check if you are authenticated before
                                proceeding{' '}
                                <a
                                  href="https://help.tresl.co/en/articles/5522116-how-to-integrate-with-google-ads"
                                  className="font-bold text-segments-link"
                                >
                                  More instructions
                                </a>{' '}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="col-span-4 mt-4 md:mt-0">
                        <div>
                          <GoogleCustomerIdSelector
                            googleCustomerIds={googleCustomerIds}
                            setGoogleCustomerIds={setGoogleCustomerIds}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between mt-8 mb-6 ">
                      <div>
                        <ButtonCTASmall
                          type="button"
                          variant="secondary"
                          onClick={handleCloseModal}
                        >
                          <p className="px-4 font-normal text-segments-13px">
                            Cancel
                          </p>
                        </ButtonCTASmall>
                      </div>
                      <div className="">
                        <ButtonCTASmall
                          variant="main"
                          type="button"
                          onClick={handleSubmit}
                          disabled={
                            !!!googleLoggedIn ||
                            isOAuthConfirmationLoading ||
                            isUpdateLoading ||
                            !!!hasSelectedCustomerIds
                          }
                        >
                          <p className="w-20 text-segments-13px">
                            {isUpdateLoading ? <SpinnerSmall /> : 'Submit'}
                          </p>
                        </ButtonCTASmall>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
